import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Ventana } from 'src/app/pages/base/ventana';
import { WhatsappTemplateService } from 'src/app/services/notificacion/whatsapp_template.service';
import { WhatsappTemplate } from '../../../../models/notificacion/whatsapp_template.model';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AppBreadcrumbService } from 'src/app/app.breadcrumb.service';
import { EmpresaService } from 'src/app/services/configuracion/empresa.service';
import { Global } from 'src/global';
import { Empresa } from 'src/app/models/configuracion/empresa.model';
import { IFlujoDefinicion } from 'src/app/models/notificacion/whatsapp_flujo.model';
import {
    contarQuickReply,
    etiquetasQueExceden,
    limiteCantidad,
    limiteEtiqueta,
} from './limites-boton.util';
import { FlujoEditorComponent } from './components/flujo/flujo-editor.component';
import { insertarVariableEnTexto } from './variable-cuerpo.util';

@Component({
  selector: 'app-whatsapp-template-ventana',
  templateUrl: './whatsapp-template-ventana.component.html',
  styleUrls: ['./whatsapp-template-ventana.component.scss']
})
export class WhatsappTemplateVentanaComponent extends Ventana implements OnInit {

    private static readonly TEMPLATE_WARNING = {
        VARIABLE_AT_START: 'variable_at_start',
        VARIABLE_AT_END: 'variable_at_end',
        CONSECUTIVE_VARIABLES: 'consecutive_variables',
        SKIPPED_VARIABLES: 'skipped_variables',
        WA_ME_LINK: 'wa_me_link',
        TOO_MANY_EMOJIS: 'too_many_emojis',
        ODD_WHITESPACE: 'odd_whitespace',
        GENERIC_MESSAGE: 'generic_message',
    } as const;

    ESTADOS = WhatsappTemplate.ESTADOS;

    // === Modelo de componentes de Meta ===
    CATEGORIAS = WhatsappTemplate.CATEGORIAS;
    FORMATOS_HEADER = WhatsappTemplate.FORMATOS_HEADER;
    TIPOS_BOTON = WhatsappTemplate.TIPOS_BOTON;
    LIMITES = WhatsappTemplate.LIMITES;

    /**
     * Botones en edición.
     *
     * Se manejan como arreglo local y se vuelcan al formulario al guardar: un
     * `FormArray` para una lista de arity variable con campos que cambian según
     * el tipo agrega complejidad sin beneficio acá.
     */
    botones: Record<string, any>[] = [];

    /** Valor INICIAL del editor de flujo, no el vivo: ese se le pide con `obtenerDefinicion()` al guardar. */
    flujoDefinicion: IFlujoDefinicion | null = null;

    /** Si la plantilla tiene respuesta automática encendida. Endurece los límites de los botones. */
    flujoActivo = false;

    @ViewChild(FlujoEditorComponent) editorFlujo?: FlujoEditorComponent;

    // Imagen
    imagenPreview = '';
    uploadedFile: File | null = null;

    /**
     * Valores de ejemplo por posición, solo para la vista previa.
     *
     * Existen para que el usuario lea "Hola Ana Gómez" en vez de "Hola {{1}}" y
     * entienda qué es una variable sin que nadie se lo explique. Al enviar se
     * reemplazan por los datos reales de cada destinatario.
     */
    private static readonly EJEMPLOS_VARIABLES = [
        'Ana Gómez', '3', '30/08/2026', '450.000',
        'ejemplo5', 'ejemplo6', 'ejemplo7', 'ejemplo8', 'ejemplo9',
    ];

    /** Formatos de encabezado que llevan un archivo adjunto. */
    private static readonly FORMATOS_MULTIMEDIA = ['IMAGE', 'VIDEO', 'DOCUMENT'];

    /** Con más de esta cantidad, WhatsApp esconde los botones sobrantes. */
    private static readonly BOTONES_SIN_ESCONDER = 3;

    private static readonly ICONO_BOTON: Record<string, string> = {
        QUICK_REPLY: 'pi pi-reply',
        URL: 'pi pi-external-link',
        PHONE_NUMBER: 'pi pi-phone',
    };

    /**
     * Bloque cuyo campo tiene el foco.
     *
     * Marca la región equivalente en la vista previa: es lo que une el campo con
     * la parte del mensaje que escribe.
     */
    bloqueEnfocado: string | null = null;

    // Variables disponibles para el template
    variablesDisponibles = [
        { nombre: 'nombre', descripcion: 'Nombre del cliente' },
        { nombre: 'monto', descripcion: 'Monto a pagar' },
        { nombre: 'fecha', descripcion: 'Fecha actual' },
        { nombre: 'cedula', descripcion: 'Cédula del cliente' },
        { nombre: 'cuota', descripcion: 'Número de cuota' },
        { nombre: 'vencimiento', descripcion: 'Fecha de vencimiento' },
    ];

    /** Posiciones que se ofrecen para insertar en el cuerpo. */
    POSICIONES_VARIABLE = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    /**
     * Si esa posición ya está usada en el mensaje.
     *
     * Repetir una posición NO agrega un dato: `{{1}}` es la posición 1 la uses
     * una vez o cinco, y quien la escribe dos veces suele creer que está pidiendo
     * dos datos distintos. Por eso la posición usada se bloquea en vez de
     * dejarla insertar de nuevo.
     */
    variableEnUso(posicion: number): boolean {
        const mensaje = String(this.formGroup?.value?.mensaje || '');
        return new RegExp(`\\{\\{\\s*${posicion}\\s*\\}\\}`).test(mensaje);
    }

    /**
     * La etiqueta de una variable, armada acá y no en el template.
     *
     * En una plantilla de Angular, `{{1}}` es una interpolación y se renderiza
     * como `1`: la forma que el usuario tiene que escribir no se puede escribir
     * literal en el HTML.
     */
    etiquetaVariable(posicion: number): string {
        return `{{${posicion}}}`;
    }

    private bloqueoInsercionVariable = false;

    @ViewChild('mensajeInput') private mensajeInputRef?: ElementRef<HTMLTextAreaElement>;

    /** Último cursor en el cuerpo. Se recuerda porque en el click de la etiqueta ya se perdió. `null` = va al final. */
    private cursorMensaje: { desde: number; hasta: number } | null = null;

    constructor(
        public router: Router,
        public messageService: MessageService,
        public activateRoute: ActivatedRoute,
        public service: WhatsappTemplateService,
        private breadcrumbService: AppBreadcrumbService,
        private empresaService: EmpresaService
    ) {
        super(router, activateRoute, messageService, service );
        const modelo = WhatsappTemplate;
        this.modulo = modelo.CODIGO_MODULO;
        this.breadcrumbService.setItems([
            { label: 'Notificacion' },
            { label: 'Plantilla de Whatsapp', routerLink: `${this.modulo}/whatsapp-template` },
            { label: 'Nuevo' },
        ]);
        this.codigoRegistro = modelo.CODIGO_REGISTRO;
        this.objClass = modelo;
        this.navigate = [this.prefixPage, this.modulo, 'whatsapp-templates'];
        this.initComponent();
    }

    ngOnInit(): void {
    }

    /**
     * Vuelca los botones guardados al arreglo de edición.
     *
     * Se llama después de `initComponent` de la clase base, que es quien carga el
     * objeto desde el servidor.
     */
    override async initComponent(): Promise<void> {
        await super.initComponent();
        this.sincronizarBotonesDesdeFormulario();
        this.sincronizarFlujoDesdeFormulario();
        this.normalizarHeaderDeLegado();
    }

    /**
     * Las plantillas con imagen de la época de Twilio no tienen `header_format`.
     *
     * Sin esto el dropdown las mostraría como "Sin encabezado", y guardar desde
     * esta ventana les borraría la imagen sin que nadie lo pida. Es el mismo
     * criterio que aplica el backend al armar la estructura para Meta.
     */
    private normalizarHeaderDeLegado(): void {
        const valor = this.formGroup?.value;

        if (!valor?.subir_imagen || !valor?.url_imagen) {
            return;
        }

        if (!valor.header_format || valor.header_format === 'NONE') {
            this.formGroup.patchValue({ header_format: 'IMAGE' }, { emitEvent: false });
        }
    }

    /**
     * Arma la definición del flujo a partir de las dos columnas que la
     * plantilla guarda: `flujo_nodo_inicial` (el id levantado) y
     * `flujo_definicion` (el jsonb con los nodos).
     */
    private sincronizarFlujoDesdeFormulario(): void {
        const valor = this.formGroup?.value;
        const nodos = valor?.flujo_definicion?.nodos;

        this.flujoDefinicion = Array.isArray(nodos) && nodos.length
            ? { nodoInicialId: valor?.flujo_nodo_inicial || '', nodos }
            : null;

        this.flujoActivo = this.flujoDefinicion !== null;
    }

    /** El flujo a guardar. Si el editor no está en pantalla conserva el valor cargado, para no borrar un flujo existente. */
    private flujoParaGuardar(): IFlujoDefinicion | null {
        return this.editorFlujo ? this.editorFlujo.obtenerDefinicion() : this.flujoDefinicion;
    }

    private sincronizarBotonesDesdeFormulario(): void {
        const guardados = this.formGroup?.value?.buttons;

        this.botones = Array.isArray(guardados)
            ? guardados.filter(Boolean).map((boton: Record<string, any>) => ({
                tipo: boton['tipo'] || 'QUICK_REPLY',
                texto: boton['texto'] || '',
                url: boton['url'] || '',
                telefono: boton['telefono'] || '',
            }))
            : [];
    }

    async onEmpresa() {
        try {
            const value = this.formGroup.value;
            let objeto = null;
            const filtros = [
                { field: 'id', value: value.id_empresa, type: 'id' },
            ];
            const res = await this.empresaService.getAll( Global.SIZE_GET_ALL, { filters: filtros } );
            if ( res.ok && res.resp.count > 0 ) {
                objeto = res.resp.rows[0];
            } else {
                this.messageService.add({severity: 'info', summary: 'Empresa no encontrada', detail: ''});
            }
            await this.setEmpresa(objeto);
        } catch (error: any) {
            this.messageService.add(this.objSession);
            setTimeout(() => {
                this.router.navigate(this.routerLogout);
            }, 4000);
        }
    }

    async setEmpresa( obj: Record<string, any> ) {
        this.formGroup.get('id_empresa').setValue(obj?.id);
    }

    agregarVariable(posicion: number, event?: Event) {
        event?.preventDefault();
        event?.stopPropagation();

        // Guarda además del bloqueo visual: el click puede llegar igual por
        // teclado o por un doble click contra el estado viejo.
        if (this.bloqueoInsercionVariable || this.variableEnUso(posicion)) {
            return;
        }

        this.bloqueoInsercionVariable = true;
        this.insertarVariableEnCursor(posicion);

        setTimeout(() => {
            this.bloqueoInsercionVariable = false;
        }, 0);
    }

    /** La primera posición que todavía no se usó, o `null` si ya están las nueve. */
    proximaVariableDisponible(): number | null {
        return this.POSICIONES_VARIABLE.find((posicion) => !this.variableEnUso(posicion)) ?? null;
    }

    /** Guarda dónde quedó el cursor: el textarea es la única fuente y se pierde al irse el foco. */
    recordarCursorMensaje(): void {
        const textarea = this.mensajeInputRef?.nativeElement;
        if (!textarea) {
            return;
        }

        this.cursorMensaje = { desde: textarea.selectionStart, hasta: textarea.selectionEnd };
    }

    /** Ctrl + Espacio inserta la próxima variable libre sin sacar las manos del teclado. */
    onKeydownMensaje(event: KeyboardEvent): void {
        if (!event.ctrlKey || event.code !== 'Space' || this.isReadonly()) {
            return;
        }

        event.preventDefault();

        const posicion = this.proximaVariableDisponible();
        if (posicion === null) {
            return;
        }

        this.insertarVariableEnCursor(posicion);
    }

    /** Inserta la variable en el cursor y lo deja justo después, con el foco todavía en el cuerpo. */
    private insertarVariableEnCursor(posicion: number): void {
        const mensajeControl = this.formGroup.get('mensaje');
        if (!mensajeControl) {
            return;
        }

        const { texto, cursor } = insertarVariableEnTexto(
            mensajeControl.value,
            this.etiquetaVariable(posicion),
            this.cursorMensaje,
        );

        mensajeControl.setValue(texto);
        this.cursorMensaje = { desde: cursor, hasta: cursor };

        const textarea = this.mensajeInputRef?.nativeElement;
        if (!textarea) {
            return;
        }

        textarea.focus();
        textarea.setSelectionRange(cursor, cursor);
    }

    /**
     * El tipo de encabezado decide todo lo demás del bloque.
     *
     * Antes había un checkbox "El template contiene imagen" separado del
     * dropdown, y los dos podían contradecirse. Ahora `header_format` es la única
     * fuente de verdad y `subir_imagen` —la bandera que el backend consulta— se
     * deriva de él.
     */
    onFormatoHeaderChange(): void {
        const esMultimedia = this.esHeaderMultimedia;

        this.formGroup.patchValue({ subir_imagen: esMultimedia }, { emitEvent: false });

        if (this.formatoHeader !== 'TEXT') {
            this.formGroup.patchValue({ header_text: null }, { emitEvent: false });
        }

        if (!esMultimedia) {
            this.imagenPreview = '';
            this.uploadedFile = null;
            this.formGroup.patchValue({ url_imagen: null }, { emitEvent: false });
        }
    }

    enfocarBloque(bloque: string): void {
        this.bloqueEnfocado = bloque;
    }

    desenfocarBloque(bloque: string): void {
        if (this.bloqueEnfocado === bloque) {
            this.bloqueEnfocado = null;
        }
    }

    onSelectImagen(event: any) {
        const file = event.files[0];
        if (file) {
            this.uploadedFile = file;
            // Crear preview
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.imagenPreview = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    }

    /** La plantilla es de autenticación: Meta define el texto y prohíbe encabezados. */
    get esAutenticacion(): boolean {
        return this.formGroup?.value?.category === 'AUTHENTICATION';
    }

    // ===================== Vista previa =====================
    //
    // El globo se arma con los mismos cuatro bloques que se le mandan a Meta y en
    // el mismo orden. Un bloque vacío no se dibuja, que es exactamente lo que
    // pasa del lado de Meta: así el usuario ve la regla en vez de leerla.

    get formatoHeader(): string {
        return String(this.formGroup?.value?.header_format || 'NONE').toUpperCase();
    }

    get esHeaderMultimedia(): boolean {
        return WhatsappTemplateVentanaComponent.FORMATOS_MULTIMEDIA.includes(this.formatoHeader);
    }

    /** "imagen", "video" o "documento", para nombrar el archivo en pantalla. */
    get nombreFormatoHeader(): string {
        const nombres: Record<string, string> = { IMAGE: 'imagen', VIDEO: 'video', DOCUMENT: 'documento' };
        return nombres[this.formatoHeader] || 'archivo';
    }

    get previewCuerpo(): string {
        return String(this.formGroup?.value?.mensaje || '').trim();
    }

    get previewHeaderTexto(): string {
        return this.formatoHeader === 'TEXT'
            ? String(this.formGroup?.value?.header_text || '').trim()
            : '';
    }

    get previewPie(): string {
        return String(this.formGroup?.value?.footer_text || '').trim();
    }

    /** Sin cuerpo no hay mensaje que mostrar: es el único bloque obligatorio. */
    get hayPreview(): boolean {
        return !!this.previewCuerpo;
    }

    /** Botones con texto. Uno a medio cargar todavía no se dibuja. */
    get previewBotones(): Record<string, any>[] {
        return this.botones.filter((boton) => String(boton?.texto || '').trim());
    }

    /**
     * Los que WhatsApp muestra sin esconder.
     *
     * Con más de tres, la aplicación deja dos a la vista y manda el resto a "Ver
     * todas las opciones". En escritorio directamente no se muestran.
     */
    get previewBotonesVisibles(): Record<string, any>[] {
        return this.hayBotonesEscondidos ? this.previewBotones.slice(0, 2) : this.previewBotones;
    }

    get hayBotonesEscondidos(): boolean {
        return this.previewBotones.length > WhatsappTemplateVentanaComponent.BOTONES_SIN_ESCONDER;
    }

    get avisoBotonesEscondidos(): string {
        return `Con ${this.previewBotones.length} botones WhatsApp muestra solo 2 y esconde el resto detrás de "Ver todas las opciones". En escritorio no se muestran.`;
    }

    iconoBoton(tipo: string): string {
        return WhatsappTemplateVentanaComponent.ICONO_BOTON[tipo] || 'pi pi-reply';
    }

    /** Los bloques que realmente se le van a mandar a Meta, en orden. */
    get bloquesEnviados(): string[] {
        const bloques: string[] = [];

        if (this.esHeaderMultimedia || this.previewHeaderTexto) {
            bloques.push('encabezado');
        }

        bloques.push('cuerpo');

        if (this.previewPie) {
            bloques.push('pie');
        }

        if (this.previewBotones.length) {
            bloques.push('botones');
        }

        return bloques;
    }

    /**
     * Parte un texto en tramos, resolviendo las variables a su valor de ejemplo.
     *
     * Devuelve tramos y no HTML a propósito: el texto lo escribe el usuario, y
     * armar la marca a mano para pasarla por `innerHTML` abriría una inyección.
     * Con tramos, Angular escapa cada uno.
     */
    tramosPreview(texto: string): { texto: string; esVariable: boolean }[] {
        const tramos: { texto: string; esVariable: boolean }[] = [];
        const regex = /{{\s*(\d+)\s*}}/g;
        let ultimo = 0;
        let match = regex.exec(texto);

        while (match !== null) {
            if (match.index > ultimo) {
                tramos.push({ texto: texto.slice(ultimo, match.index), esVariable: false });
            }

            const posicion = Number(match[1]);
            const ejemplo = WhatsappTemplateVentanaComponent.EJEMPLOS_VARIABLES[posicion - 1];
            tramos.push({ texto: ejemplo || `ejemplo${posicion}`, esVariable: true });

            ultimo = match.index + match[0].length;
            match = regex.exec(texto);
        }

        if (ultimo < texto.length) {
            tramos.push({ texto: texto.slice(ultimo), esVariable: false });
        }

        return tramos;
    }

    /**
     * Al pasar a autenticación se limpian los componentes que Meta no admite.
     *
     * Dejarlos cargados haría que la plantilla se rechace días después, cuando ya
     * nadie recuerda qué se tocó.
     */
    onCategoriaChange(): void {
        if (!this.esAutenticacion) {
            return;
        }

        this.formGroup.patchValue({ header_format: 'NONE' });
        this.onFormatoHeaderChange();
        this.botones = [];
        this.resincronizarFlujoConBotones();
    }

    agregarBoton(): void {
        if (!this.puedeAgregarBoton) {
            return;
        }

        this.botones.push({ tipo: 'QUICK_REPLY', texto: '', url: '', telefono: '' });
        this.resincronizarFlujoConBotones();
    }

    /**
     * Le pide al editor de flujo que rehaga las opciones del nodo inicial.
     *
     * `botones` se edita mutando el arreglo en el lugar (push/splice/`ngModel`
     * sobre `boton.texto`), así que su referencia nunca cambia y el `@Input`
     * de `app-flujo-editor` no dispara `ngOnChanges` solo. Por eso el padre
     * pide la resincronización directo en cada punto donde cambia un botón.
     */
    resincronizarFlujoConBotones(): void {
        this.editorFlujo?.sincronizarNodoInicialConBotones();
    }

    /** Caracteres permitidos por botón: 20 con respuesta automática, 25 sin ella. */
    get limiteEtiquetaBoton(): number {
        return limiteEtiqueta(this.flujoActivo, this.LIMITES.BOTON_ETIQUETA);
    }

    /** Quick-reply permitidos: 3 con respuesta automática, los de plantilla sin ella. */
    get limiteQuickReply(): number {
        return limiteCantidad(this.flujoActivo, this.LIMITES.BOTONES);
    }

    get cantidadQuickReply(): number {
        return contarQuickReply(this.botones);
    }

    get puedeAgregarBoton(): boolean {
        if (this.botones.length >= this.LIMITES.BOTONES) {
            return false;
        }

        return this.cantidadQuickReply < this.limiteQuickReply;
    }

    /** Los quick-reply que ya no entran: `maxlength` no recorta lo escrito antes de prender el flujo. */
    get botonesQueExcedenElFlujo(): string[] {
        return etiquetasQueExceden(this.botones, this.flujoActivo);
    }

    /** Si sobran quick-reply. Quitar uno es decisión del usuario, no se recorta solo. */
    get sobranQuickReplyParaElFlujo(): boolean {
        return this.flujoActivo && this.cantidadQuickReply > this.limiteQuickReply;
    }

    quitarBoton(indice: number): void {
        this.botones.splice(indice, 1);
        this.resincronizarFlujoConBotones();
    }

    /**
     * Deja los botones listos para el backend: descarta los vacíos y quita los
     * campos que no corresponden a cada tipo.
     */
    private botonesParaGuardar(): Record<string, any>[] {
        return this.botones
            .filter((boton) => String(boton?.texto || '').trim())
            .map((boton) => {
                const base: Record<string, any> = {
                    tipo: boton.tipo,
                    texto: String(boton.texto || '').trim(),
                };

                if (boton.tipo === 'URL') {
                    base['url'] = String(boton.url || '').trim();
                }

                if (boton.tipo === 'PHONE_NUMBER') {
                    base['telefono'] = String(boton.telefono || '').trim();
                }

                return base;
            });
    }

    private async fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event: any) => {
                resolve(event.target.result);
            };
            reader.onerror = () => reject(new Error('No se pudo leer la imagen seleccionada'));
            reader.readAsDataURL(file);
        });
    }

    async guardar() {
        if (this.formGroup.invalid) {
            await this.setInvalidControls();
            this.messageService.add({ severity: 'error', summary: 'Formulario Inválido', detail: 'Complete los campos obligatorios' });
            return;
        }

        if (!await this.check()) {
            return;
        }

        this.cargando = true;
        await this.actualizarAuditoria();
        await this.objeto.actualizarDatos(this.formGroup.value);

        // `getRawValue` y no `value`: los controles deshabilitados —el nombre
        // técnico de Meta— quedarían fuera de `value`.
        const payload = { ...this.formGroup.getRawValue() };
        if (this.uploadedFile) {
            payload.imagen_base64 = await this.fileToBase64(this.uploadedFile);
        }

        // Los botones se editan en un arreglo local: se vuelcan acá, ya limpios.
        payload.buttons = this.botonesParaGuardar();
        payload.flujo_definicion = this.flujoParaGuardar();

        // `header_format` es la fuente de verdad del encabezado. `subir_imagen`
        // sigue existiendo porque el backend la consulta para el camino
        // multimedia, pero ya no es un control aparte que pueda contradecirlo.
        payload.subir_imagen = this.esHeaderMultimedia;

        this.objeto.serverJson = payload;

        let resp = null;
        if (this.objeto.id) {
            resp = await this.objeto.actualizar(this.service);
        } else {
            resp = await this.objeto.guardar(this.service);
        }

        this.cargando = false;

        if (resp.ok) {
            this.messageService.add({ severity: 'success', summary: 'Registro Guardado', detail: 'Se guardó correctamente' });
            setTimeout(() => {
                this.volver();
            }, 1000);
            return;
        }

        this.messageService.add({ severity: 'error', summary: resp.msg, detail: this.detalleDeError(resp.resp), life: 10000 });
    }

    /**
     * El backend puede devolver el detalle como una lista de cosas a corregir
     * (`detallesParaUsuario`) o como un texto suelto. Se normaliza a un string
     * con un ítem por línea para que el toast las muestre todas.
     */
    private detalleDeError(detalle: unknown): string {
        if (Array.isArray(detalle)) {
            return detalle.map((item) => `• ${item}`).join('\n');
        }

        return typeof detalle === 'string' ? detalle : '';
    }

    get templateWarnings(): string[] {
        return this.generarObservacionesTemplate(this.formGroup?.get('mensaje')?.value || '');
    }

    async sincronizarEstadoTemplate() {
        if (!this.formGroup.value.id) {
            return;
        }

        const response = await this.service.sincronizarEstado(this.formGroup.value.id, this.formGroup.value.id_empresa);
        if (!response.ok) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: response.msg || 'No se pudo sincronizar el estado' });
            return;
        }

        this.formGroup.patchValue({
            ...response.resp,
            estado: WhatsappTemplate.normalizarEstado(response.resp?.estado),
            approve_status: WhatsappTemplate.normalizarApproveStatus(response.resp?.approve_status),
        });
        this.messageService.add({ severity: 'success', summary: 'Estado actualizado', detail: 'Se sincronizó el estado con Meta' });
    }

    get approveStatusNormalizado(): string {
        return WhatsappTemplate.normalizarApproveStatus(this.formGroup?.value?.approve_status);
    }

    get approveStatusLabel(): string {
        const labels: Record<string, string> = {
            draft: 'Borrador',
            pending: 'Pendiente',
            approved: 'Aprobado',
            rejected: 'Rechazado',
            error: 'Error',
            // Le pegan a plantillas ya aprobadas. Meta las manda por el webhook.
            in_appeal: 'En apelación',
            paused: 'Pausada por Meta',
            disabled: 'Deshabilitada por Meta',
            locked: 'Bloqueada por Meta',
            flagged: 'Observada por Meta',
            limit_exceeded: 'Límite excedido',
            archived: 'Archivada',
            deleted: 'Eliminada en Meta',
            pending_deletion: 'Pendiente de eliminación',
            unknown: 'Estado desconocido',
        };

        return labels[this.approveStatusNormalizado] || labels.pending;
    }

    /**
     * Solo `approved` habilita el envío.
     *
     * Se usa para el color del tag: pintar de verde una plantilla pausada haría
     * creer que se puede usar.
     */
    get plantillaUtilizable(): boolean {
        return this.approveStatusNormalizado === 'approved';
    }

    async cerrarOnLista( obj?: Record<string, any> ): Promise<void> {
        await super.cerrarOnLista( obj );
        if ( this.objClass === Empresa && obj ) {
            await this.setEmpresa(obj);
        }
        this.objClass = null;
    }

    async onListaEmpresa() {
        await super.onLista( this.empresaService, Empresa, Empresa.NOMBRE_PLURAL );
    }

    private generarObservacionesTemplate(mensaje: string): string[] {
        const normalizedMessage = String(mensaje || '');
        if (!normalizedMessage.trim()) {
            return [];
        }

        const warnings = new Map<string, string>();
        const trimmedMessage = normalizedMessage.trim();
        const variableMatches = this.obtenerMatchesVariables(normalizedMessage);
        const variableNumbers = Array.from(
            new Set(variableMatches.map((match) => Number(match[1]))),
        ).sort((first, second) => first - second);

        if (/^{{\s*\d+\s*}}/.test(trimmedMessage)) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.VARIABLE_AT_START,
                'Hay una variable al inicio del mensaje. Meta suele rechazar este formato.',
            );
        }

        if (/{{\s*\d+\s*}}$/.test(trimmedMessage)) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.VARIABLE_AT_END,
                'Hay una variable al final del mensaje. Meta suele rechazar este formato.',
            );
        }

        if (/}}\s*{{/.test(normalizedMessage)) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.CONSECUTIVE_VARIABLES,
                'Se detectaron variables consecutivas (por ejemplo {{1}}{{2}}). Conviene separarlas con texto o puntuación.',
            );
        }

        if (variableNumbers.length > 0) {
            const maxVariable = variableNumbers[variableNumbers.length - 1];
            const missingVariables: number[] = [];
            for (let index = 1; index <= maxVariable; index++) {
                if (!variableNumbers.includes(index)) {
                    missingVariables.push(index);
                }
            }

            if (missingVariables.length > 0) {
                warnings.set(
                    WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.SKIPPED_VARIABLES,
                    `La numeración de variables está salteada. Faltan: ${missingVariables.map((value) => `{{${value}}}`).join(', ')}.`,
                );
            }
        }

        if (/wa\.me\//i.test(normalizedMessage)) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.WA_ME_LINK,
                'Se detectó un enlace wa.me/. WhatsApp puede observar o rechazar templates con este formato.',
            );
        }

        if (this.contarEmojis(normalizedMessage) > 10) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.TOO_MANY_EMOJIS,
                'Se detectaron más de 10 emojis. Conviene simplificar el mensaje para evitar observaciones.',
            );
        }

        if (this.tieneWhitespaceRaro(normalizedMessage)) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.ODD_WHITESPACE,
                'Se detectaron tabs o espacios poco prolijos. Conviene limpiar el texto antes de enviarlo.',
            );
        }

        if (this.esMensajeGenerico(normalizedMessage, variableMatches.length)) {
            warnings.set(
                WhatsappTemplateVentanaComponent.TEMPLATE_WARNING.GENERIC_MESSAGE,
                'El mensaje parece muy genérico. Es solo una observación heurística: conviene hacerlo más específico para la aprobación.',
            );
        }

        return Array.from(warnings.values());
    }

    private contarEmojis(mensaje: string): number {
        const emojiRegex = /\p{Extended_Pictographic}/gu;
        return mensaje.match(emojiRegex)?.length || 0;
    }

    private obtenerMatchesVariables(mensaje: string): RegExpExecArray[] {
        const regex = /{{\s*(\d+)\s*}}/g;
        const matches: RegExpExecArray[] = [];
        let match = regex.exec(mensaje);

        while (match !== null) {
            matches.push(match);
            match = regex.exec(mensaje);
        }

        return matches;
    }

    private tieneWhitespaceRaro(mensaje: string): boolean {
        return /\t/.test(mensaje)
            || / {3,}/.test(mensaje)
            || /\u00A0/.test(mensaje)
            || /\n\s*\n\s*\n/.test(mensaje)
            || /(^|\n)\s+|\s+($|\n)/.test(mensaje);
    }

    private esMensajeGenerico(mensaje: string, variableCount: number): boolean {
        const sanitized = mensaje
            .replace(/{{\s*\d+\s*}}/g, ' ')
            .replace(/https?:\/\/\S+/gi, ' ')
            .replace(/[^\p{L}\p{N}\s]/gu, ' ')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();

        if (!sanitized) {
            return true;
        }

        const genericPatterns = [
            /^hola$/,
            /^hola cliente$/,
            /^estimado cliente$/,
            /^mensaje de prueba$/,
            /^recordatorio$/,
            /^notificacion$/,
            /^aviso$/,
            /^gracias$/,
        ];

        if (genericPatterns.some((pattern) => pattern.test(sanitized))) {
            return true;
        }

        const words = sanitized.split(' ').filter(Boolean);
        return variableCount <= 1 && words.length <= 3;
    }
}
